/**
 * Learning-independence tier — classifies a student by how much of their lesson activity is
 * self-directed practice ("latihan") versus teacher-assigned work ("tugas").
 *
 * Canonical formula (owned by the metrics team):
 *
 *   total = latihan_count + tugas_count
 *   total === 0        → not_started          ("Kurang aktif")
 *   total  <  5        → insufficient_data     ("Kurang aktif")
 *   else share = latihan / total
 *     share < 0.50     → guided               ("Terbimbing")
 *     share < 0.67     → balanced             ("Seimbang")
 *     share < 0.80     → self_directed         ("Mandiri")
 *     else             → highly_self_directed  ("Sangat mandiri")
 *
 * Single source of truth: the server uses `share` for the "Kemandirian" sort key, the client renders
 * the badge label. Keep this pure and isomorphic.
 */

export type IndependenceTier =
  | 'not_started'
  | 'insufficient_data'
  | 'guided'
  | 'balanced'
  | 'self_directed'
  | 'highly_self_directed'

const TIER_LABELS: Record<IndependenceTier, string> = {
  not_started: 'Kurang aktif',
  insufficient_data: 'Kurang aktif',
  guided: 'Terbimbing',
  balanced: 'Seimbang',
  self_directed: 'Mandiri',
  highly_self_directed: 'Sangat mandiri',
}

/** Minimum combined activity before a share is meaningful enough to classify. */
export const INDEPENDENCE_MIN_TOTAL = 5

export interface IndependenceTierInput {
  /** Teacher-assigned enrollments (type LESSON). */
  tugasCount: number
  /** Self-directed practice enrollments (type PRACTICE). */
  latihanCount: number
}

export interface IndependenceTierResult {
  tier: IndependenceTier
  label: string
  /** latihan / total. `null` when total < INDEPENDENCE_MIN_TOTAL (tier is undefined-by-data). */
  share: number | null
  total: number
}

export function getIndependenceTier({ tugasCount, latihanCount }: IndependenceTierInput): IndependenceTierResult {
  const total = tugasCount + latihanCount

  if (total === 0) return { tier: 'not_started', label: TIER_LABELS.not_started, share: null, total }
  if (total < INDEPENDENCE_MIN_TOTAL) {
    return { tier: 'insufficient_data', label: TIER_LABELS.insufficient_data, share: null, total }
  }

  const share = latihanCount / total
  let tier: IndependenceTier
  if (share < 0.5) tier = 'guided'
  else if (share < 0.67) tier = 'balanced'
  else if (share < 0.8) tier = 'self_directed'
  else tier = 'highly_self_directed'

  return { tier, label: TIER_LABELS[tier], share, total }
}
